// Общие поля форм блоков. Не входят в публичный контракт зоны — используются
// только формами внутри src/ui/forms.
import type { Currency } from '../../types';
import { useStore } from '../../store';
import { useT } from '../../i18n';
import styles from './fields.module.css';

type InputRef = React.Ref<HTMLInputElement>;
type SelectRef = React.Ref<HTMLSelectElement>;

interface FieldChromeProps {
  id: string;
  label: string;
  error?: string;
  warning?: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}

function FieldChrome({ id, label, error, warning, hint, required, children }: FieldChromeProps) {
  const errorId = `${id}-error`;
  const warnId = `${id}-warning`;
  return (
    <div className={styles.field}>
      <label htmlFor={id} className="field-label">
        {label}
        {required ? ' *' : ''}
      </label>
      {children}
      {error && (
        <p id={errorId} className="field-error">
          {error}
        </p>
      )}
      {!error && warning && (
        <p id={warnId} className={styles.warning}>
          {warning}
        </p>
      )}
      {!error && !warning && hint && <p className={styles.hint}>{hint}</p>}
    </div>
  );
}

export function describedBy(id: string, error?: string, warning?: string): string | undefined {
  if (error) return `${id}-error`;
  if (warning) return `${id}-warning`;
  return undefined;
}

export interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  error?: string;
  warning?: string;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  uppercase?: boolean;
  maxLength?: number;
  inputRef?: InputRef;
  autoFocus?: boolean;
  listId?: string;
  list?: readonly string[];
}

export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  warning,
  hint,
  required,
  placeholder,
  uppercase,
  maxLength,
  inputRef,
  autoFocus,
  listId,
  list,
}: TextFieldProps) {
  return (
    <FieldChrome id={id} label={label} error={error} warning={warning} hint={hint} required={required}>
      <input
        id={id}
        ref={inputRef}
        className="input"
        type="text"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        autoFocus={autoFocus}
        list={listId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, warning)}
        onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
      />
      {list && listId && (
        <datalist id={listId}>
          {list.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      )}
    </FieldChrome>
  );
}

export interface NumberFieldProps {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  error?: string;
  hint?: string;
  required?: boolean;
  min?: number;
  step?: number;
  inputRef?: InputRef;
}

export function NumberField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  required,
  min,
  step,
  inputRef,
}: NumberFieldProps) {
  return (
    <FieldChrome id={id} label={label} error={error} hint={hint} required={required}>
      <input
        id={id}
        ref={inputRef}
        className="input"
        type="number"
        value={value}
        min={min}
        step={step}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error)}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldChrome>
  );
}

export interface DateTimeFieldProps {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  error?: string;
  required?: boolean;
  inputRef?: InputRef;
}

export function DateTimeField({ id, label, value, onChange, error, required, inputRef }: DateTimeFieldProps) {
  return (
    <FieldChrome id={id} label={label} error={error} required={required}>
      <input
        id={id}
        ref={inputRef}
        className="input"
        type="datetime-local"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error)}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldChrome>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  selectRef?: SelectRef;
  autoFocus?: boolean;
}

export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  hint,
  required,
  selectRef,
  autoFocus,
}: SelectFieldProps) {
  return (
    <FieldChrome id={id} label={label} error={error} hint={hint} required={required}>
      <select
        id={id}
        ref={selectRef}
        className="select"
        value={value}
        autoFocus={autoFocus}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error)}
        onChange={(e) => onChange(e.target.value)}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldChrome>
  );
}

export function CurrencySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Currency;
  onChange(c: Currency): void;
}) {
  const { currencies } = useStore();
  const t = useT();
  return (
    <select
      id={id}
      className="select"
      value={value}
      onChange={(e) => onChange(e.target.value as Currency)}
      aria-label={t('toolbar.currency')}
    >
      {currencies.map((c) => (
        <option key={c.code} value={c.code}>
          {c.code}
        </option>
      ))}
    </select>
  );
}

export interface MoneyFieldProps {
  idPrefix: string;
  label: string;
  amount: string;
  onAmountChange(v: string): void;
  currency: Currency;
  onCurrencyChange(c: Currency): void;
  error?: string;
  required?: boolean;
  inputRef?: InputRef;
}

export function MoneyField({
  idPrefix,
  label,
  amount,
  onAmountChange,
  currency,
  onCurrencyChange,
  error,
  required,
  inputRef,
}: MoneyFieldProps) {
  const id = `${idPrefix}-amount`;
  return (
    <FieldChrome id={id} label={label} error={error} required={required}>
      <div className={styles.moneyRow}>
        <input
          id={id}
          ref={inputRef}
          className={`input ${styles.moneyAmount}`}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={amount}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error)}
          onChange={(e) => onAmountChange(e.target.value)}
        />
        <div className={styles.moneyCurrency}>
          <CurrencySelect id={`${idPrefix}-currency`} value={currency} onChange={onCurrencyChange} />
        </div>
      </div>
    </FieldChrome>
  );
}

export function Checkbox({
  id,
  label,
  checked,
  onChange,
  disabled = false,
  hint,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
  disabled?: boolean;
  /** Пояснение под чекбоксом: почему выключен или что произойдёт при выборе. */
  hint?: string;
}) {
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.checkboxLabel}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
      {hint && (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      )}
    </div>
  );
}

export function Stepper({
  id,
  label,
  value,
  onChange,
  min = 1,
  disabled = false,
}: {
  id: string;
  label: string;
  value: number;
  onChange(v: number): void;
  min?: number;
  disabled?: boolean;
}) {
  const t = useT();
  return (
    <FieldChrome id={id} label={label}>
      <div className={styles.stepper}>
        <button
          type="button"
          className={`btn ${styles.stepperBtn}`}
          aria-label={t('form.decrease')}
          disabled={disabled}
          onClick={() => onChange(Math.max(min, value - 1))}
        >
          −
        </button>
        <input
          id={id}
          className={`input ${styles.stepperInput} mono`}
          type="number"
          min={min}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange(Number.isFinite(n) ? Math.max(min, n) : min);
          }}
        />
        <button
          type="button"
          className={`btn ${styles.stepperBtn}`}
          aria-label={t('form.increase')}
          disabled={disabled}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </FieldChrome>
  );
}

export function NoteField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange(v: string): void;
}) {
  const t = useT();
  return (
    <FieldChrome id={id} label={t('field.note')}>
      <textarea
        id={id}
        className="textarea"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldChrome>
  );
}
